import { apiClient } from "@toss-ads/ui";
import type {
  BreakdownRow,
  ContactAttempt,
  TimeseriesBucket,
  TimeseriesPoint,
} from "@toss-ads/ui";
import type {
  Advertiser,
  CreateAdvertiserInput,
  CreateUserInput,
  PaginatedUsers,
  Platform,
  UpdateAdvertiserInput,
  UserRow,
} from "./types";

export interface ListUsersParams {
  q?: string;
  role?: "user" | "admin" | "";
  status?: "active" | "disabled" | "";
  limit?: number;
  offset?: number;
}

export function listUsers(params: ListUsersParams): Promise<PaginatedUsers> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.role) qs.set("role", params.role);
  if (params.status) qs.set("status", params.status);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  return apiClient<PaginatedUsers>(`/api/admin/users?${qs}`);
}

export function getUser(id: string): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}`);
}

export function createUser(dto: CreateUserInput): Promise<UserRow> {
  return apiClient<UserRow>("/api/admin/users", { method: "POST", body: dto });
}

export function setUserDisabled(id: string, disabled: boolean): Promise<UserRow> {
  return apiClient<UserRow>(
    `/api/admin/users/${id}/${disabled ? "disable" : "enable"}`,
    { method: "PATCH" },
  );
}

export function setUserRole(
  id: string,
  role: "user" | "admin" | "developer",
): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}/role`, {
    method: "PATCH",
    body: { role },
  });
}

export function setUserNote(id: string, note: string | null): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}/note`, {
    method: "PATCH",
    body: { note },
  });
}

export function setUserColor(
  id: string,
  color: string | null,
): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}/color`, {
    method: "PATCH",
    body: { color },
  });
}

export function setUserPhone(
  id: string,
  phone: string | null,
): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}/phone`, {
    method: "PATCH",
    body: { phone },
  });
}

export function setUserSmsOnLead(
  id: string,
  smsOnLead: boolean,
): Promise<UserRow> {
  return apiClient<UserRow>(`/api/admin/users/${id}/sms`, {
    method: "PATCH",
    body: { smsOnLead },
  });
}

export interface ImpersonateResponse {
  accessToken: string;
  targetEmail: string;
  targetRole: "user" | "admin" | "developer";
  expiresInSeconds: number;
}

export function impersonateUser(id: string): Promise<ImpersonateResponse> {
  return apiClient<ImpersonateResponse>(`/api/admin/users/${id}/impersonate`, {
    method: "POST",
  });
}

// ===== user_advertisers =====

export function listUserAdvertisers(userId: string): Promise<string[]> {
  return apiClient<{ advertiserIds: string[] }>(
    `/api/admin/users/${userId}/accounts`,
  ).then((r) => r.advertiserIds);
}

export function setUserAdvertisers(
  userId: string,
  advertiserIds: string[],
): Promise<string[]> {
  return apiClient<{ advertiserIds: string[] }>(
    `/api/admin/users/${userId}/accounts`,
    { method: "PATCH", body: { advertiserIds } },
  ).then((r) => r.advertiserIds);
}

// ===== advertisers =====

export function listAdvertisers(): Promise<Advertiser[]> {
  return apiClient<Advertiser[]>("/api/admin/accounts");
}

export function getAdvertiser(id: string): Promise<Advertiser> {
  return apiClient<Advertiser>(`/api/admin/accounts/${id}`);
}

export function createAdvertiser(
  dto: CreateAdvertiserInput,
): Promise<Advertiser> {
  return apiClient<Advertiser>("/api/admin/accounts", {
    method: "POST",
    body: dto,
  });
}

export function updateAdvertiser(
  id: string,
  dto: UpdateAdvertiserInput,
): Promise<Advertiser> {
  return apiClient<Advertiser>(`/api/admin/accounts/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deleteAdvertiser(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/accounts/${id}`, {
    method: "DELETE",
  });
}

// ===== admins (외부/내부 어드민 마스터, developer 전용) =====

export interface Admin {
  id: string;
  slug: string;
  label: string | null;
  createdAt: string;
}

export interface CreateAdminInput {
  label?: string | null;
}

export function listAdmins(): Promise<Admin[]> {
  return apiClient<Admin[]>("/api/admin/admins");
}

export function createAdmin(dto: CreateAdminInput): Promise<Admin> {
  return apiClient<Admin>("/api/admin/admins", { method: "POST", body: dto });
}

export interface UpdateAdminInput {
  label?: string | null;
}

export function updateAdmin(
  id: string,
  dto: UpdateAdminInput,
): Promise<Admin> {
  return apiClient<Admin>(`/api/admin/admins/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deleteAdmin(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/admins/${id}`, {
    method: "DELETE",
  });
}

// ===== external sources (developer 전용) =====

export interface ExternalSource {
  id: string;
  advertiserId: string;
  advertiserSlug: string;
  advertiserName: string;
  adminId: string | null;
  slug: string;
  label: string | null;
  active: boolean;
  createdAt: string;
  lastLeadAt: string | null;
}

// 생성 응답 — apiKey 는 이때만 노출.
export interface ExternalSourceWithKey {
  source: Partial<ExternalSource> & { id: string; slug: string };
  apiKey: string | null;
}

export interface CreateExternalSourceInput {
  advertiserId: string;
  label?: string | null;
}

export function listExternalSources(): Promise<ExternalSource[]> {
  return apiClient<ExternalSource[]>("/api/admin/external-sources");
}

export function createExternalSource(
  dto: CreateExternalSourceInput,
): Promise<ExternalSourceWithKey> {
  return apiClient<ExternalSourceWithKey>("/api/admin/external-sources", {
    method: "POST",
    body: dto,
  });
}

export interface UpdateExternalSourceInput {
  active?: boolean;
  label?: string | null;
}

export function updateExternalSource(
  id: string,
  dto: UpdateExternalSourceInput,
): Promise<ExternalSource> {
  return apiClient<ExternalSource>(`/api/admin/external-sources/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deleteExternalSource(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/external-sources/${id}`, {
    method: "DELETE",
  });
}

// ===== kakao 비즈폼+ 연동 (developer 전용) =====

export interface KakaoAccount {
  id: string;
  adAccountId: string;
  label: string | null;
  restApiKey: string;
  status: string; // pending | connected | error
  scopes: string | null;
  accessExpiresAt: string | null;
  lastError: string | null;
  adminId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KakaoForm {
  id: string;
  accountId: string;
  advertiserId: string | null;
  advertiserName: string | null;
  formId: string;
  label: string | null;
  source: string | null;
  sourceLabel: string | null;
  platformId: string | null;
  channel: string;
  active: boolean;
  lastPolledAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface CreateKakaoAccountInput {
  adAccountId: string;
  label?: string;
  restApiKey: string;
  clientSecret: string;
  adminId?: string;
}

export interface CreateKakaoFormInput {
  accountId: string;
  advertiserId: string;
  formId: string;
  label?: string;
  source?: string;
  sourceLabel?: string;
  platformId: string;
  channel?: string;
}

export function listKakaoAccounts(): Promise<KakaoAccount[]> {
  return apiClient<KakaoAccount[]>("/api/admin/kakao/accounts");
}

export function createKakaoAccount(
  dto: CreateKakaoAccountInput,
): Promise<KakaoAccount> {
  return apiClient<KakaoAccount>("/api/admin/kakao/accounts", {
    method: "POST",
    body: dto,
  });
}

export function updateKakaoAccount(
  id: string,
  dto: {
    label?: string;
    clientSecret?: string;
    adminId?: string;
  },
): Promise<KakaoAccount> {
  return apiClient<KakaoAccount>(`/api/admin/kakao/accounts/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deleteKakaoAccount(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/kakao/accounts/${id}`, {
    method: "DELETE",
  });
}

export function getKakaoAuthorizeUrl(id: string): Promise<{ url: string }> {
  return apiClient<{ url: string }>(
    `/api/admin/kakao/accounts/${id}/authorize`,
  );
}

// 연결된 모든 계정 소재 크롤 → 신규 폼 자동 추가(광고주 미배정). 추가 개수 반환.
export function syncKakaoForms(): Promise<{
  added: number;
  deactivated: number;
}> {
  return apiClient<{ added: number; deactivated: number }>(
    "/api/admin/kakao/forms/sync",
    { method: "POST" },
  );
}

export interface KakaoPollRun {
  status: string; // ok | error | skipped
  error: string | null;
  pages: number;
  fetched: number;
  inserted: number;
  duplicated: number;
  failed: number;
}

// 폼 단건 수동 수집(재시도). 로그는 자동 폴링과 동일하게 남는다.
export function pollKakaoFormNow(id: string): Promise<KakaoPollRun> {
  return apiClient<KakaoPollRun>(`/api/admin/kakao/forms/${id}/poll`, {
    method: "POST",
  });
}

export interface KakaoPollLog {
  id: string;
  status: string; // ok | error | skipped
  pages: number;
  fetched: number;
  inserted: number;
  duplicated: number;
  failed: number;
  durationMs: number | null;
  cursorBefore: string | null;
  cursorAfter: string | null;
  error: string | null;
  createdAt: string;
}

// 폼별 폴링 실행 이력(최신순, 보관 2주).
export function listKakaoFormLogs(
  id: string,
  limit = 50,
): Promise<KakaoPollLog[]> {
  return apiClient<KakaoPollLog[]>(
    `/api/admin/kakao/forms/${id}/logs?limit=${limit}`,
  );
}

export function listKakaoForms(): Promise<KakaoForm[]> {
  return apiClient<KakaoForm[]>("/api/admin/kakao/forms");
}

export function createKakaoForm(
  dto: CreateKakaoFormInput,
): Promise<{ id: string }> {
  return apiClient<{ id: string }>("/api/admin/kakao/forms", {
    method: "POST",
    body: dto,
  });
}

export function updateKakaoForm(
  id: string,
  dto: {
    advertiserId?: string;
    label?: string;
    source?: string;
    sourceLabel?: string;
    platformId?: string;
    channel?: string;
    active?: boolean;
  },
): Promise<{ id: string }> {
  return apiClient<{ id: string }>(`/api/admin/kakao/forms/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deleteKakaoForm(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/kakao/forms/${id}`, {
    method: "DELETE",
  });
}

// ===== summaries =====

export interface AdvertiserLeadSummary {
  advertiserId: string;
  total: number;
  testTotal: number;
  deletedTotal: number;
  latestSubmitTime: string | null;
}

export function fetchAdvertiserSummariesAdmin(): Promise<AdvertiserLeadSummary[]> {
  return apiClient<AdvertiserLeadSummary[]>("/api/leads/admin/summaries");
}

export interface PlatformInfo {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
}

// platform null = 자연유입.
export interface PlatformLeadSummary {
  platform: PlatformInfo | null;
  total: number;
  latestSubmitTime: string | null;
}

export function fetchAdminPlatformSummaries(
  advertiserId: string,
): Promise<PlatformLeadSummary[]> {
  const qs = new URLSearchParams({ account_id: advertiserId });
  return apiClient<PlatformLeadSummary[]>(
    `/api/leads/admin/platform-summaries?${qs}`,
  );
}

export interface MediaOption {
  key: string;
  label: string;
  total: number;
}

export function fetchAdminStatusTimeseries(
  advertiserId: string,
  params: {
    from?: string;
    to?: string;
    bucket: TimeseriesBucket;
    assignee?: string;
  },
): Promise<TimeseriesPoint[]> {
  const qs = new URLSearchParams({
    account_id: advertiserId,
    bucket: params.bucket,
  });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.assignee) qs.set("assignee", params.assignee);
  return apiClient<TimeseriesPoint[]>(
    `/api/leads/admin/status-timeseries?${qs}`,
  );
}

export function fetchAdminStatusBreakdown(
  advertiserId: string,
  params: { from?: string; to?: string; group: "assignee" | "media" },
): Promise<BreakdownRow[]> {
  const qs = new URLSearchParams({
    account_id: advertiserId,
    group: params.group,
  });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiClient<BreakdownRow[]>(
    `/api/leads/admin/status-breakdown?${qs}`,
  );
}

export interface ContactTime {
  avgSeconds: number | null;
  sampleCount: number;
}

export function fetchAdminContactTime(
  advertiserId: string,
  params: { from?: string; to?: string },
): Promise<ContactTime> {
  const qs = new URLSearchParams({ account_id: advertiserId });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiClient<ContactTime>(`/api/leads/admin/contact-time?${qs}`);
}

export function fetchAdminMediaOptions(
  advertiserId: string,
  params: { from?: string; to?: string } = {},
): Promise<MediaOption[]> {
  const qs = new URLSearchParams({ account_id: advertiserId });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiClient<MediaOption[]>(`/api/leads/admin/media-options?${qs}`);
}

export interface SourceDisplay {
  slug: string;
  label: string | null;
}

export function fetchSources(): Promise<SourceDisplay[]> {
  return apiClient<SourceDisplay[]>("/api/leads/sources");
}

// ===== leads (admin) =====

import type { LeadStatus } from "@toss-ads/ui";
export type { LeadStatus };

export interface AdminLead {
  id: string;
  advertiserId: string;
  platformId: string | null;
  leadSubmitTime: string;
  status: LeadStatus;
  // 외부 webhook 벤더 식별(매칭된 API key 라벨). 없으면 null.
  source: string | null;
  // 벤더가 보낸 유입경로 원문. 아직 platform 매핑이 없는 신규 매체도 이 값으로 표기.
  channel: string | null;
  // 담당자(사무장). 미배정이면 null.
  assigneeId: string | null;
  assigneeEmail: string | null;
  assigneeNote: string | null;
  assigneeColor: string | null;
  // 연락 시도(부재/단선) 로그.
  contactAttempts: ContactAttempt[];
  // 인입 시 알림 SMS 가 실제로 전송된 인원 수(전송 성공분).
  smsRecipientCount: number;
  // 알림 대상(사무장) 목록 — 상세 모달용. 전송비율 = count/length.
  smsRecipients: Array<{ name: string; phone: string }>;
  submittedContent?: unknown;
  userColumnData?: unknown;
}

export interface PaginatedAdminLeads {
  items: AdminLead[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminLeadsParams {
  advertiserId?: string;
  // 플랫폼 uuid 또는 'organic'(자연유입). 미지정 시 전체. (레거시)
  platformId?: string;
  // 매체 필터 키 ('plat:...' | 'ext:...'). platformId 대체.
  media?: string;
  status?: LeadStatus;
  // 'unassigned'(미배정) | 유저 uuid. 미지정 시 전체.
  assignee?: string;
  // 인입 DB 검색.
  name?: string;
  contact?: string;
  from?: string;
  to?: string;
  isTest?: boolean;
  // soft-delete 된 리드만 조회(삭제됨 뷰).
  deleted?: boolean;
  limit?: number;
  offset?: number;
}

function adminLeadsQuery(p: AdminLeadsParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (p.advertiserId) qs.set("account_id", p.advertiserId);
  if (p.platformId) qs.set("platform_id", p.platformId);
  if (p.media) qs.set("media", p.media);
  if (p.status) qs.set("status", p.status);
  if (p.assignee) qs.set("assignee", p.assignee);
  if (p.name) qs.set("name", p.name);
  if (p.contact) qs.set("contact", p.contact);
  if (p.from) qs.set("from", p.from);
  if (p.to) qs.set("to", p.to);
  if (p.isTest !== undefined) qs.set("is_test", String(p.isTest));
  if (p.deleted) qs.set("deleted", "true");
  if (p.limit !== undefined) qs.set("limit", String(p.limit));
  if (p.offset !== undefined) qs.set("offset", String(p.offset));
  return qs;
}

export function fetchAdminLeads(
  p: AdminLeadsParams,
): Promise<PaginatedAdminLeads> {
  return apiClient<PaginatedAdminLeads>(`/api/leads/admin?${adminLeadsQuery(p)}`);
}

// 리드 직접 추가 (운영자 수동 입력). 기존 인입 파이프라인으로 태움.
export interface CreateManualLeadInput {
  advertiserId: string;
  name: string;
  phone: string;
  email?: string;
  channel?: string;
  submittedAt?: string;
  answers?: Record<string, string>;
}

export function createManualLead(
  dto: CreateManualLeadInput,
): Promise<{ ok: boolean; leadId: string }> {
  return apiClient<{ ok: boolean; leadId: string }>("/api/admin/manual-leads", {
    method: "POST",
    body: dto,
  });
}

// 부재 목록(admin) — 서버가 부재/단선 & 7회 미만으로 강제 필터.
export function fetchAdminAbsentLeads(
  p: AdminLeadsParams,
): Promise<PaginatedAdminLeads> {
  return apiClient<PaginatedAdminLeads>(
    `/api/leads/admin/absent?${adminLeadsQuery(p)}`,
  );
}

export function setLeadStatusAdmin(
  id: string,
  status: LeadStatus,
): Promise<AdminLead> {
  return apiClient<AdminLead>(`/api/leads/${id}/status`, {
    method: "PATCH",
    body: { status },
  });
}

export interface Assignee {
  id: string;
  email: string;
  note: string | null;
  color: string | null;
}

export function fetchAssignees(accountId: string): Promise<Assignee[]> {
  return apiClient<Assignee[]>(`/api/leads/assignees?account_id=${accountId}`);
}

export function setLeadAssigneeAdmin(
  id: string,
  assigneeId: string | null,
): Promise<AdminLead> {
  return apiClient<AdminLead>(`/api/leads/${id}/assignee`, {
    method: "PATCH",
    body: { assigneeId },
  });
}

export function removeContactAttemptAdmin(
  id: string,
  at: string,
): Promise<AdminLead> {
  return apiClient<AdminLead>(`/api/leads/${id}/contact-attempts/remove`, {
    method: "PATCH",
    body: { at },
  });
}

export function deleteLeadAdmin(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/leads/${id}`, { method: "DELETE" });
}

export function restoreLeadAdmin(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/leads/${id}/restore`, {
    method: "PATCH",
  });
}

// ===== audit log (변경 기록, developer 전용) =====

export interface AuditEntry {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  action: "create" | "update" | "delete" | "revert";
  entityType: string;
  entityId: string;
  before: unknown;
  after: unknown;
  summary: string | null;
  revertedAt: string | null;
  revertible: boolean;
  createdAt: string;
}

export interface PaginatedAuditEntries {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export function listAuditEntries(params: {
  limit?: number;
  offset?: number;
  entityType?: string;
}): Promise<PaginatedAuditEntries> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.entityType) qs.set("entityType", params.entityType);
  return apiClient<PaginatedAuditEntries>(`/api/admin/audit?${qs}`);
}

export function revertAuditEntry(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/audit/${id}/revert`, {
    method: "POST",
  });
}

// ===== platforms =====

export function listPlatforms(): Promise<Platform[]> {
  return apiClient<Platform[]>("/api/admin/platforms");
}

export interface CreatePlatformInput {
  slug: string;
  name: string;
  logoUrl?: string;
}

export function createPlatform(dto: CreatePlatformInput): Promise<Platform> {
  return apiClient<Platform>("/api/admin/platforms", {
    method: "POST",
    body: dto,
  });
}

export interface UpdatePlatformInput {
  name?: string;
  logoUrl?: string;
}

export function updatePlatform(
  id: string,
  dto: UpdatePlatformInput,
): Promise<Platform> {
  return apiClient<Platform>(`/api/admin/platforms/${id}`, {
    method: "PATCH",
    body: dto,
  });
}

export function deletePlatform(id: string): Promise<{ ok: boolean }> {
  return apiClient<{ ok: boolean }>(`/api/admin/platforms/${id}`, {
    method: "DELETE",
  });
}
